import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { Compass, ArrowLeft, ArrowRight, Wrench, RotateCcw, CheckCircle2, Mail } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const NAMES = { residential: 'Residential', 'small-business': 'Small Business', 'semi-commercial': 'Semi Commercial' };
const BLURB = {
  residential: 'Home and hobby-scale freeze dryers, rebuilt and dialed back in.',
  'small-business': 'Larger machines for side income and small production, made reliable again.',
  'semi-commercial': 'Production floor units rebuilt for uptime and throughput.',
};

const QUESTIONS = [
  {
    key: 'use', label: 'Where do you use your freeze dryer?',
    options: [
      { label: 'Home, hobby or personal use', value: 'residential' },
      { label: 'Small business or side income', value: 'small-business' },
      { label: 'Commercial or full production', value: 'semi-commercial' },
    ],
  },
  {
    key: 'size', label: 'About how big is your machine?',
    options: [
      { label: 'Small or medium (countertop, up to ~10 trays)', value: 'Medium' },
      { label: 'Large or extra large batches', value: 'Large' },
      { label: 'Semi commercial / production floor', value: 'Semi commercial / production' },
    ],
  },
  {
    key: 'symptom', label: "What's going wrong?",
    options: [
      { label: "Won't pull vacuum", value: "Won't pull vacuum" },
      { label: 'Not freezing / not cold', value: "Won't freeze / not cold" },
      { label: 'Error code or display issue', value: 'Error code or display issue' },
      { label: 'Leaking (water or oil)', value: 'Leaking (water or oil)' },
      { label: 'Loud or failing pump', value: 'Loud or failing pump' },
      { label: 'Product still wet after a cycle', value: 'Product still wet after a cycle' },
      { label: 'Something else', value: 'Something else' },
    ],
  },
  {
    key: 'urgency', label: 'How urgent is it?',
    options: [
      { label: 'Still running but struggling', value: 'Still running but struggling' },
      { label: 'Down right now', value: 'Down right now' },
      { label: "Been down over a week", value: 'Been down over a week' },
    ],
  },
];

const recommendSlug = (a) => {
  let slug = a.use || 'residential';
  if (slug === 'residential' && a.size === 'Semi commercial / production') slug = 'small-business';
  return slug;
};

export const MachineSelectorQuiz = () => {
  const navigate = useNavigate();
  const [step, setStep] = useState(0);
  const [answers, setAnswers] = useState({});
  const [done, setDone] = useState(false);
  const [email, setEmail] = useState('');
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);
  const [started, setStarted] = useState(false);
  const [leadCaptured, setLeadCaptured] = useState(false);

  const total = QUESTIONS.length;
  const q = QUESTIONS[step];

  const trackQuiz = (event) => {
    axios.post(`${API_URL}/api/quiz/track?event=${event}`).catch(() => {});
  };

  const choose = (value) => {
    if (!started) { trackQuiz('start'); setStarted(true); }
    const next = { ...answers, [q.key]: value };
    setAnswers(next);
    if (step < total - 1) setStep(step + 1);
    else { setDone(true); trackQuiz('complete'); }
  };

  const reset = () => { setStep(0); setAnswers({}); setDone(false); setEmail(''); setSent(false); setLeadCaptured(false); };

  const captureLead = async () => {
    if (!email || !email.includes('@') || leadCaptured) return;
    setLeadCaptured(true);
    const slug = recommendSlug(answers);
    const params = new URLSearchParams({ prefilled: '1' });
    if (answers.symptom) params.set('symptom', answers.symptom);
    if (answers.size) params.set('size', answers.size);
    if (answers.urgency) params.set('urgency', answers.urgency);
    if (email) params.set('email', email);
    const quoteUrl = `${window.location.origin}/machines/${slug}?${params.toString()}`;
    const notes = `Quiz completed, recommended ${NAMES[slug]} rebuild. Use: ${answers.use || ''} · Size: ${answers.size || ''} · Symptom: ${answers.symptom || ''} · Urgency: ${answers.urgency || ''}`;
    try {
      await axios.post(`${API_URL}/api/leads/capture`, { email, lead_type: 'quiz', source: 'machine-quiz', notes, recommended_slug: slug, quote_url: quoteUrl });
      trackQuiz('lead');
    } catch (err) { /* non-blocking */ }
  };

  const emailResults = async () => {
    if (!email || !email.includes('@')) { toast.error('Please enter a valid email.'); return; }
    setSending(true);
    await captureLead();
    setSending(false);
    setSent(true);
    toast.success("Saved! We'll follow up with your recommendation.");
  };

  const startQuote = () => {
    if (email && email.includes('@')) captureLead();
    const slug = recommendSlug(answers);
    const params = new URLSearchParams({ prefilled: '1' });
    if (answers.symptom) params.set('symptom', answers.symptom);
    if (answers.size) params.set('size', answers.size);
    if (answers.urgency) params.set('urgency', answers.urgency);
    if (email) params.set('email', email);
    navigate(`/machines/${slug}?${params.toString()}`);
  };

  const slug = recommendSlug(answers);

  return (
    <section className="py-20 section-padding bg-[#003456] relative overflow-hidden" id="quiz" data-testid="machine-quiz">
      <div className="absolute inset-0 opacity-[0.06]" style={{ backgroundImage: 'radial-gradient(circle at 15% 25%, #CE8946 0, transparent 40%), radial-gradient(circle at 85% 75%, #CE8946 0, transparent 45%)' }} />
      <div className="max-w-3xl mx-auto relative">
        <div className="text-center mb-8">
          <span className="inline-flex items-center gap-2 bg-[#CE8946]/15 text-[#CE8946] px-4 py-1.5 rounded-full text-xs font-bold uppercase tracking-[0.2em] border border-[#CE8946]/30">
            <Compass className="h-4 w-4" /> 30 second quiz
          </span>
          <h2 className="text-3xl sm:text-4xl font-bold text-white mt-5">Which service do I need?</h2>
          <p className="text-white/70 mt-3">Answer a few quick questions and we'll point you to the right rebuild, with your quote pre-filled.</p>
        </div>

        <div className="bg-[#023F63] border border-white/10 rounded-2xl p-7 sm:p-9 shadow-2xl">
          {!done ? (
            <div data-testid="quiz-question">
              {/* progress */}
              <div className="flex items-center gap-1.5 mb-6">
                {QUESTIONS.map((_, i) => (
                  <div key={i} className={`h-1.5 rounded-full flex-1 transition-all ${i <= step ? 'bg-[#CE8946]' : 'bg-white/15'}`} />
                ))}
              </div>
              <p className="text-white/50 text-xs font-semibold uppercase tracking-wider mb-2">Question {step + 1} of {total}</p>
              <h3 className="text-xl font-bold text-white mb-5">{q.label}</h3>
              <div className="grid gap-2.5">
                {q.options.map((o, i) => (
                  <button
                    key={o.value}
                    onClick={() => choose(o.value)}
                    className={`text-left text-sm px-4 py-3.5 rounded-lg border transition-colors ${answers[q.key] === o.value ? 'bg-[#CE8946] border-[#CE8946] text-[#003456] font-semibold' : 'bg-white/5 border-white/20 text-white/85 hover:border-[#CE8946]/60 hover:bg-white/10'}`}
                    data-testid={`quiz-option-${step}-${i}`}
                  >
                    {o.label}
                  </button>
                ))}
              </div>
              {step > 0 && (
                <button onClick={() => setStep(step - 1)} className="inline-flex items-center gap-1.5 text-white/60 hover:text-white text-sm mt-5 transition-colors" data-testid="quiz-back">
                  <ArrowLeft className="h-4 w-4" /> Back
                </button>
              )}
            </div>
          ) : (
            <div className="text-center" data-testid="quiz-result">
              <CheckCircle2 className="h-12 w-12 text-[#CE8946] mx-auto mb-3" />
              <p className="text-white/60 text-sm">Based on your answers, we recommend</p>
              <h3 className="text-2xl font-extrabold text-white mt-1" data-testid="quiz-recommendation">{NAMES[slug]} rebuild service</h3>
              <p className="text-white/70 text-sm mt-2 max-w-md mx-auto">{BLURB[slug]}</p>

              <div className="bg-white/5 border border-white/10 rounded-xl p-4 mt-6 text-left max-w-sm mx-auto space-y-1.5">
                {answers.symptom && <p className="text-sm text-white/80 flex justify-between"><span className="text-white/50">Issue</span><span className="font-medium">{answers.symptom}</span></p>}
                {answers.size && <p className="text-sm text-white/80 flex justify-between"><span className="text-white/50">Size</span><span className="font-medium">{answers.size}</span></p>}
                {answers.urgency && <p className="text-sm text-white/80 flex justify-between"><span className="text-white/50">Status</span><span className="font-medium">{answers.urgency}</span></p>}
              </div>

              {/* Email capture so a completed quiz becomes a lead */}
              <div className="max-w-sm mx-auto mt-6" data-testid="quiz-email-capture">
                {sent ? (
                  <p className="inline-flex items-center gap-2 text-sm font-semibold text-white" data-testid="quiz-email-sent">
                    <CheckCircle2 className="h-5 w-5 text-[#CE8946]" /> Saved! We'll follow up with your recommendation.
                  </p>
                ) : (
                  <>
                    <label className="block text-sm text-white/70 mb-2 flex items-center gap-2"><Mail className="h-4 w-4 text-[#CE8946]" /> Save your results and we'll follow up</label>
                    <div className="flex flex-col sm:flex-row gap-2">
                      <input
                        type="email"
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        placeholder="you@email.com"
                        className="flex-1 px-4 py-3 rounded-md bg-white/10 border border-white/20 text-white placeholder:text-white/50 text-sm focus:outline-none focus:ring-2 focus:ring-[#CE8946]"
                        data-testid="quiz-email-input"
                      />
                      <button
                        onClick={emailResults}
                        disabled={sending}
                        className="inline-flex items-center justify-center gap-2 border border-[#CE8946] text-[#CE8946] hover:bg-[#CE8946] hover:text-[#003456] font-bold px-5 py-3 rounded-md transition-colors whitespace-nowrap disabled:opacity-60"
                        data-testid="quiz-email-submit"
                      >
                        {sending ? 'Saving…' : 'Email me my results'}
                      </button>
                    </div>
                  </>
                )}
              </div>

              <div className="flex flex-col sm:flex-row gap-3 justify-center mt-7">
                <button onClick={startQuote} className="inline-flex items-center justify-center gap-2 bg-[#CE8946] hover:bg-[#B87333] text-[#003456] font-bold px-7 py-3.5 rounded-md transition-colors" data-testid="quiz-start-quote">
                  <Wrench className="h-5 w-5" /> Start my free quote <ArrowRight className="h-4 w-4" />
                </button>
                <button onClick={reset} className="inline-flex items-center justify-center gap-2 border border-white/25 text-white font-semibold px-6 py-3.5 rounded-md hover:bg-white/10 transition-colors" data-testid="quiz-retake">
                  <RotateCcw className="h-4 w-4" /> Retake
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </section>
  );
};

export default MachineSelectorQuiz;
